import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { 
  getPortfolios, 
  createPortfolio as createPortfolioApi,
  renamePortfolio as renamePortfolioApi,
  deletePortfolio as deletePortfolioApi 
} from '../api';
import { AuthContext } from './AuthContext';

export const PortfolioContext = createContext();

export const PortfolioProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [portfolios, setPortfolios] = useState([]);
  const [activePortfolio, setActivePortfolioState] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPortfolios = useCallback(async () => {
    if (!user) {
      setPortfolios([]);
      setActivePortfolioState(null);
      setLoading(false);
      return;
    }
    try {
      const res = await getPortfolios();
      setPortfolios(res.data);

      if (res.data.length > 0) {
        const savedId = localStorage.getItem('activePortfolioId');
        const found = res.data.find(p => p._id === savedId);
        
        if (savedId === 'all') {
          setActivePortfolioState({ _id: 'all', name: 'All Portfolios', type: 'ALL' });
        } else if (found) {
          setActivePortfolioState(found);
        } else {
          // Default to All Portfolios
          setActivePortfolioState({ _id: 'all', name: 'All Portfolios', type: 'ALL' });
          localStorage.setItem('activePortfolioId', 'all');
        }
      } else {
        setActivePortfolioState(null);
      }
    } catch (err) {
      console.error('Failed to fetch portfolios', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const setActivePortfolio = (portfolio) => {
    setActivePortfolioState(portfolio);
    if (portfolio) {
      localStorage.setItem('activePortfolioId', portfolio._id);
    }
  };

  const createPortfolio = async (nameOrData, maybeType) => {
    const payload = typeof nameOrData === 'object' 
      ? nameOrData 
      : { name: nameOrData, type: maybeType || 'BROKERAGE' };
    const res = await createPortfolioApi(payload);
    const newPortfolio = res.data;
    setPortfolios(prev => [...prev, newPortfolio]);
    // Auto-select the new portfolio
    setActivePortfolio(newPortfolio);
    return newPortfolio;
  };

  const updatePortfolio = async (id, nameOrData, maybeType) => {
    const payload = typeof nameOrData === 'object'
      ? nameOrData
      : { name: nameOrData, ...(maybeType ? { type: maybeType } : {}) };
    const res = await renamePortfolioApi(id, payload);
    const updated = res.data;
    setPortfolios(prev => prev.map(p => p._id === id ? updated : p));
    if (activePortfolio && activePortfolio._id === id) {
      setActivePortfolioState(updated);
    }
    return updated;
  };

  const renamePortfolio = updatePortfolio;

  const deletePortfolio = async (id) => {
    const res = await deletePortfolioApi(id);
    setPortfolios(prev => {
      const updated = prev.filter(p => p._id !== id);
      if (activePortfolio && activePortfolio._id === id) {
        const nextActive = updated.length > 0 ? updated[0] : null;
        setActivePortfolio(nextActive);
        if (!nextActive) {
          localStorage.removeItem('activePortfolioId');
        }
      }
      return updated;
    });
    return res.data;
  };

  const removePortfolioFromState = (id) => {
    setPortfolios(prev => prev.filter(p => p._id !== id));
    if (activePortfolio && activePortfolio._id === id) {
      const remaining = portfolios.filter(p => p._id !== id);
      setActivePortfolio(remaining.length > 0 ? remaining[0] : null);
    }
  };

  return (
    <PortfolioContext.Provider value={{
      portfolios,
      activePortfolio,
      setActivePortfolio,
      createPortfolio,
      renamePortfolio,
      updatePortfolio,
      deletePortfolio,
      removePortfolioFromState,
      fetchPortfolios,
      loading
    }}>
      {children}
    </PortfolioContext.Provider>
  );
};
